/**
 * OC Chat Providers Component
 * Consolidates all context providers needed for OC chat functionality
 */

import React, { FC, ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';

import { AppContextProvider } from '../../../../store/App/store';
import { TranscriptContextProvider, TranscriptContextValue } from '../../../../store/transcript/store';
import { IAppTheme } from '../../../../styles/IAppTheme';
import { AppEndpoints } from '../../../../types/app';
import { OcChatConfig } from '../../../../types/oc';
import { getOcDefaultInitialMessage } from '../../config/ocDefaults';
import { useOcFabWidgetOptions } from '../../contexts/OcFabWidgetOptionsContext';
import { OcAskHumanProvider } from '../AskHuman/OcAskHumanProvider';
import { FeedbackProvider } from '../Feedback/FeedbackProvider';
import { CrawlerJobsProvider } from '../Sources/CrawlerJobProvider';
    
export interface OcChatProvidersProps {
  /** AI Assistant endpoints configuration */
  endpoints: AppEndpoints;
  /** App configuration (systemId, tenantId, userId) */
  appConfig: {
    systemId: string;
    tenantId: string;
    userId: string;
  };
  /** OC chat configuration */
  config: OcChatConfig;
  /** Theme configuration */
  theme: IAppTheme;
  /** Initial session ID */
  initialSessionKey?: string;
  /** Chat context ref for session management */
  chatContextRef?: React.MutableRefObject<TranscriptContextValue | null>;
  /** Child components */
  children: ReactNode;
}

/**
 * OcChatProviders Component
 * 
 * Wraps all necessary context providers for OC chat functionality:
 * - AppContextProvider: App configuration and endpoints
 * - TranscriptContextProvider: Chat transcript state
 * - ThemeProvider: Styled-components theme
 * - FeedbackProvider: Feedback functionality
 * - OcAskHumanProvider: Ask Human functionality
 * 
 * Note: NotificationProvider is at the OcFabWidget level for global positioning
 * ```
 */
export const OcChatProviders: FC<OcChatProvidersProps> = ({
  endpoints,
  appConfig,
  config,
  theme,
  initialSessionKey,
  chatContextRef,
  children,
}) => {
  const { welcomeMessage } = useOcFabWidgetOptions();
  // Build initial and placeholder message
  const initialMessage = getOcDefaultInitialMessage(welcomeMessage);
    
  return (
    <AppContextProvider endpoints={endpoints} config={appConfig}>
      <TranscriptContextProvider
        initialSessionId={initialSessionKey || undefined}
        initialMessage={initialMessage}
        chatContextRef={chatContextRef}
      >
        <ThemeProvider theme={theme}>
          <CrawlerJobsProvider config={config} >
            <FeedbackProvider config={config}>
              <OcAskHumanProvider config={config}>
                {children}
              </OcAskHumanProvider>
            </FeedbackProvider>
          </CrawlerJobsProvider>
        </ThemeProvider>
      </TranscriptContextProvider>
    </AppContextProvider>
  );
};

export default OcChatProviders;

